import React, { Fragment, useEffect, useRef, useState } from "react";
import { Tabs, Tab } from "react-bootstrap";
import CustomModal from "./CustomModal";
import { useDispatch, useSelector } from "react-redux";
import {
  createNewImage,
  fetchDeleteMedia,
  fetchMediaList,
} from "../../../actions/media";
import CloseIcon from "@mui/icons-material/Close";
import { Button, CircularProgress } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const MediaPickerModal = ({ show, onClose, onSelect, multiple = false }) => {
  const dispatch = useDispatch();
  const { mediaList } = useSelector((state) => state.mediaReducer);

  const [tab, setTab] = useState("library");
  const [uploading, setUploading] = useState(false);

  // ✅ selected: object (single) ili array (multiple)
  const [selected, setSelected] = useState(multiple ? [] : null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState(null);

  useEffect(() => {
    if (!show) return;
    dispatch(fetchMediaList());
  }, [show, dispatch]);

  useEffect(() => {
    if (!show) return;
    // reset selekcije kad se modal otvori ili promeni multiple
    setSelected(multiple ? [] : null);
    setTab("library");
  }, [show, multiple]);

  const handleUploadMultiple = async (files) => {
    if (!files?.length) return;

    setUploading(true);
    try {
      await dispatch(createNewImage(files));
      // nakon upload-a prebacujemo na biblioteku i refetch
      setTab("library");
      dispatch(fetchMediaList());
    } finally {
      setUploading(false);
    }
  };

  const isSelected = (m) => {
    if (!multiple) return selected?.id === m.id;
    return (selected || []).some((x) => x.id === m.id);
  };

  const toggleSelect = (m) => {
    if (!multiple) {
      setSelected(m);
      return;
    }

    setSelected((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const exists = arr.some((x) => x.id === m.id);
      return exists ? arr.filter((x) => x.id !== m.id) : [...arr, m];
    });
  };

  const submitHandler = () => {
    if (!multiple) {
      if (!selected) return;
      onSelect(selected); // ✅ jedan objekat
      onClose();
      return;
    }

    if (!(selected || []).length) return;
    onSelect(selected); // ✅ niz objekata
    onClose();
  };

  const confirmDeleteImage = async () => {
    if (!mediaToDelete) return;

    await dispatch(fetchDeleteMedia(mediaToDelete.id));
    dispatch(fetchMediaList());

    // ako je obrisana selektovana slika, očisti selekciju
    setSelected((prev) => {
      if (!multiple) {
        return prev?.id === mediaToDelete.id ? null : prev;
      }
      return (prev || []).filter((x) => x.id !== mediaToDelete.id);
    });

    setShowDeleteConfirm(false);
    setMediaToDelete(null);
  };

  const cancelDeleteImage = () => {
    setShowDeleteConfirm(false);
    setMediaToDelete(null);
  };

  const selectedCount = multiple ? (selected || []).length : selected ? 1 : 0;

  return (
    <Fragment>
      <CustomModal
        show={show}
        hideModal={onClose}
        modalTitle="Upravljanje medijima"
        actionSubmitModal={submitHandler}
        size="xl"
        footerContent={true}
      >
        <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-3">
          <Tab eventKey="library" title="Biblioteka">
            {multiple && (
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
                Selektovano: {selectedCount}
              </div>
            )}

            <div className="media-grid">
              {mediaList?.map((m) => {
                const active = isSelected(m);

                return (
                  <div
                    key={m.id}
                    className={`media-item ${active ? "active" : ""}`}
                    onClick={() => toggleSelect(m)}
                    role="button"
                    style={{ position: "relative" }}
                  >
                    {/* ✅ checkbox indikator za multi-select */}
                    {multiple && (
                      <div
                        style={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          width: 18,
                          height: 18,
                          borderRadius: 4,
                          border: "2px solid #fff",
                          background: active
                            ? "rgba(0,0,0,0.65)"
                            : "rgba(0,0,0,0.25)",
                          zIndex: 2,
                        }}
                      />
                    )}

                    <img src={m?.file} alt={m.name || ""} />
                    <span>{m?.name}</span>

                    <div
                      className="media-delete-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMediaToDelete(m);
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <CloseIcon />
                    </div>
                  </div>
                );
              })}
            </div>
          </Tab>

          <Tab eventKey="upload" title="Upload novih slika">
            <MultiUpload
              onUpload={handleUploadMultiple}
              uploading={uploading}
              label="Odaberite slike za upload"
            />
          </Tab>
        </Tabs>
      </CustomModal>

      <CustomModal
        show={showDeleteConfirm}
        hideModal={cancelDeleteImage}
        modalTitle="Potvrda brisanja"
        actionSubmitModal={confirmDeleteImage}
        size="sm"
      >
        <p>
          Da li ste sigurni da želite da obrišete sliku
          <b> {mediaToDelete?.name}</b>?
        </p>
      </CustomModal>
    </Fragment>
  );
};

export default MediaPickerModal;

// Minimalna upload komponenta (multi)
const MultiUpload = ({ label, onUpload, uploading }) => {
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    try {
      await onUpload(files);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    } catch (err) {
      // opcionalno: toast
    }
  };

  return (
    <div
      style={{
        padding: "20px",
        border: "2px dashed #e5e7eb",
        borderRadius: "12px",
        textAlign: "center",
        background: "#fafafa",
      }}
    >
      <p style={{ marginBottom: 15, fontWeight: 500 }}>{label}</p>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*"
        hidden
        onChange={handleChange}
      />

      <Button
        variant="contained"
        startIcon={<CloudUploadIcon />}
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        sx={{
          borderRadius: "8px",
          padding: "8px 20px",
          textTransform: "none",
          fontWeight: 600,
        }}
      >
        {uploading ? (
          <>
            <CircularProgress size={18} sx={{ color: "white", mr: 1 }} />
            Upload u toku...
          </>
        ) : (
          "Izaberi slike"
        )}
      </Button>

      <div
        style={{
          fontSize: 12,
          color: "#6b7280",
          marginTop: 10,
        }}
      >
        Podržani formati: JPG, PNG, WEBP, SVG
      </div>
    </div>
  );
};
